import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import GenericDraggableTableView from "../../../Features/Table/GenericDraggableTable";
import { useAccessGrantedEntitiesData } from "./hooks/useAccessGrantedEntitiesData";
import { useAccessGrantedEntitiesColumns } from "./hooks/useAccessGrantedEntitiesColumns";
import NameTheCSV from "../../../Helper/CSVOperations/NameTheCSV";
import { genericSearchSuggestions } from "../../../Api/DigitalTwinListApi";
import { encryptTheParams } from "../../../Helper/QueryParams/EncryptDecrypt";
import { getPagesMetaData, getFilterData } from "../../../Api/Api";
import CODE from "../../../Static/Constants/StatusCodes";
import { SetSessionExpired } from "../../../Actions";

const SEARCHABLE_COLUMN_KEYS = ["licensePlateNo", "deviceID"];

const DEFAULT_ACCESS_GRANTED_TABLE = {
  columnsConfig: {},
  columnOrdering: [],
};

const searchSuggestionsHandler = (query, columnKey, limit) => {
  return genericSearchSuggestions(query, columnKey, limit);
};

const ROWS_PER_PAGE = 10;

const AccessGrantedEntitiesContent = ({ apiKeyId, baseEntityPluralLabel = "Entities" }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [tableConfig, setTableConfig] = useState(DEFAULT_ACCESS_GRANTED_TABLE);
  const [isMetaLoading, setIsMetaLoading] = useState(true);
  const [filters, setFilters] = useState({});
  const [selectedFilters, setSelectedFilters] = useState({});

  useEffect(() => {
    const loadPageMeta = async () => {
      setIsMetaLoading(true);
      const res = await getPagesMetaData("ClientApiManagement");

      if (res?.responseStatus?.code === CODE.SUCCESS) {
        const metaInfo = res.response?.response?.metaInfo || {};
        const tableConf =
          metaInfo.table?.AccessGrantedVehicleTable || DEFAULT_ACCESS_GRANTED_TABLE;
        setTableConfig(tableConf);

        const filterConfigsArray =
          metaInfo.filterConfigurations?.AccessGrantedVehicleTable?.filters || [];

        if (filterConfigsArray.length > 0) {
          const builtFilters = {};
          await Promise.all(
            filterConfigsArray.map(async (fc) => {
              let values = [];
              if (fc.type === "static") {
                values = fc.staticData || [];
              } else {
                const filterRes = await getFilterData(fc);
                if (filterRes?.responseStatus?.code === CODE.SUCCESS) {
                  values = filterRes.response?.response?.filterData || [];
                }
              }
              builtFilters[fc.key] = {
                key: fc.key,
                label: fc.label,
                displayType: fc.displayType,
                values,
              };
            })
          );
          setFilters(builtFilters);
        }
      } else if (res?.responseStatus?.code === CODE.SESSION_EXPIRED) {
        dispatch(SetSessionExpired(true));
      }

      setIsMetaLoading(false);
    };

    loadPageMeta();
  }, [dispatch]);

  const handleApplyFilters = useCallback((newFilters) => {
    setSelectedFilters(newFilters);
  }, []);

  const onBusNumberClick = (record) => {
    encryptTheParams(
      {
        batteryID: record.batteryID,
        deviceID: record.deviceID,
        batterySearchedGlobal: "false",
        prevLink: "/",
        navigateFromTab: true,
        tabValue: "overview",
      },
      navigate,
      true  // specificBattery=true + navigateFromTab=true → opens /specificBattery in new tab
    );
  };

  const {
    pagedData,
    total,
    isLoading,
    pageNumber,
    setPageNumber,
    applySearch,
    getAllRows,
  } = useAccessGrantedEntitiesData(apiKeyId, tableConfig, selectedFilters);

  const {
    allColumns,
    visibleColumns,
    setVisibleColumns,
    selectedColumnKeys,
    defaultColumnKeys,
    applySelectedColumns,
  } = useAccessGrantedEntitiesColumns(tableConfig, onBusNumberClick);

  const searchableFields = useMemo(() => {
    const { columnOrdering = [], columnsConfig = {} } = tableConfig || {};

    return columnOrdering.reduce((acc, columnKey) => {
      const config = columnsConfig[columnKey];
      const dataIndex = config?.key || columnKey;

      if (!config || !SEARCHABLE_COLUMN_KEYS.includes(dataIndex)) return acc;

      acc.push({ key: dataIndex, title: config.title });
      return acc;
    }, []);
  }, [tableConfig]);

  const getCSVData = async () => {
    // Snapshot columns before awaiting so a mid-download column-visibility change
    // doesn't produce a mismatched CSV.
    const columnsSnapshot = visibleColumns;
    const rows = await getAllRows();
    return rows.map((row) =>
      Object.fromEntries(
        columnsSnapshot.map((col) => [col.headerLabel, row[col.dataIndex] ?? ""])
      )
    );
  };

  const getCsvColumnSettings = () => ({
    columnOrder: visibleColumns.map((col) => col.headerLabel),
  });

  return (
    <GenericDraggableTableView
      title={`Access Granted ${baseEntityPluralLabel}`}
      count={total}
      showPrimaryButton={false}
      tableColumns={visibleColumns}
      initialColumns={allColumns}
      selectedColumns={selectedColumnKeys}
      defaultSelectedColumns={defaultColumnKeys}
      applySelectedColumns={applySelectedColumns}
      onColumnOrderChange={(newCols) => setVisibleColumns(newCols)}
      tableData={pagedData}
      isLoading={isLoading || isMetaLoading}
      pageNumber={pageNumber}
      rowsPerPage={ROWS_PER_PAGE}
      total={total}
      onPageChange={(page) => setPageNumber(page)}
      applySearch={applySearch}
      disableSearch={isMetaLoading || searchableFields.length === 0}
      searchableFields={searchableFields}
      searchSuggestionsHandler={searchSuggestionsHandler}
      showEmailComposer={false}
      getCSVData={getCSVData}
      getCsvColumnSettings={getCsvColumnSettings}
      getCSVFileName={() =>
        NameTheCSV("AccessGrantedEntities", `Access Granted ${baseEntityPluralLabel}`)
      }
      alwaysEnableActions={true}
      filters={filters}
      selectedFilters={selectedFilters}
      applyFilters={handleApplyFilters}
      emptyMessage={`No ${baseEntityPluralLabel.toLowerCase()} found`}
    />
  );
};

export default AccessGrantedEntitiesContent;
